import React, { useCallback, useEffect, useRef, useState } from "react";
import { Autocomplete, CircularProgress, TextField, Typography } from "@mui/material";
import { useLazyGetSpecificMultiLevelFieldsQuery } from "../Services/settingsAPIs/CommonAPI";
import { TicketMultiLevelPathItem } from "../utils/multiLevelFieldValue";

interface MultiLevelOption {
  id: number;
  choice: string;
  field_label?: string;
}

interface LazyMultiLevelDropdownProps {
  field: any;
  value?: string | null;
  ticketPath?: TicketMultiLevelPathItem[] | null;
  onFinalSelect?: (lastChoice: MultiLevelOption, path?: string) => void;
  className?: string;
  error?: string | null;
  prefillFromTicketValue?: boolean;
}

function normalizePath(value: string): string {
  return value
    .split(/>\s*/)
    .map((part) => part.trim())
    .filter(Boolean)
    .join(">");
}

function findChoiceMatch(
  options: MultiLevelOption[],
  choice: string
): MultiLevelOption | undefined {
  const target = choice.trim().toLowerCase();
  return options.find((opt) => opt.choice?.trim().toLowerCase() === target);
}

function isSyntheticOption(option?: MultiLevelOption): boolean {
  return Boolean(option && option.id < 0);
}

const MAX_API_LEVEL = 4;

export const LazyMultiLevelDropdown: React.FC<LazyMultiLevelDropdownProps> = ({
  field,
  value,
  ticketPath,
  onFinalSelect,
  className,
  error,
  prefillFromTicketValue = false,
}) => {
  const [fetchLevelOptions] = useLazyGetSpecificMultiLevelFieldsQuery();
  const [levelOptions, setLevelOptions] = useState<(MultiLevelOption[] | undefined)[]>([
    undefined,
  ]);
  const [levelLabels, setLevelLabels] = useState<Record<number, string>>({});
  const [selected, setSelected] = useState<MultiLevelOption[]>([]);
  const [loadingLevel, setLoadingLevel] = useState<number | null>(null);
  const selectedPathRef = useRef<string | null>(null);
  const isSyntheticPrefillRef = useRef(false);
  const hasTicketPathPrefillRef = useRef(false);

  const getLevelLabel = (levelIndex: number) => {
    if (levelIndex === 0) {
      return field?.label || "";
    }
    return levelLabels[levelIndex] || "";
  };

  const finalizeSelection = (lastChoice: MultiLevelOption, path: string) => {
    selectedPathRef.current = path;
    onFinalSelect?.(lastChoice, path);
  };

  const loadLevel = useCallback(
    async (parentId: number, apiLevel: number, levelIndex: number) => {
      setLoadingLevel(levelIndex);
      try {
        const result = await fetchLevelOptions({
          fieldId: parentId,
          level: apiLevel,
        }).unwrap();
        const options = Array.isArray(result) ? result : [];
        setLevelOptions((prev) => {
          const next = [...prev];
          next[levelIndex] = options;
          return next;
        });
        if (levelIndex > 0 && options[0]?.field_label) {
          setLevelLabels((prev) => ({
            ...prev,
            [levelIndex]: options[0].field_label as string,
          }));
        }
        return options;
      } catch {
        setLevelOptions((prev) => {
          const next = [...prev];
          next[levelIndex] = [];
          return next;
        });
        return [];
      } finally {
        setLoadingLevel(null);
      }
    },
    [fetchLevelOptions]
  );

  const applyTicketPathPrefill = useCallback((pathItems: TicketMultiLevelPathItem[]) => {
    const selectedOptions = pathItems.map((item) => ({
      id: item.id,
      choice: item.choice,
      field_label: item.label,
    }));
    const labels: Record<number, string> = {};

    pathItems.forEach((item, index) => {
      if (index > 0) {
        labels[index] = item.label;
      }
    });

    setSelected(selectedOptions);
    setLevelOptions(selectedOptions.map((option) => [option]));
    setLevelLabels(labels);
    selectedPathRef.current = selectedOptions.map((item) => item.choice).join(">");
    isSyntheticPrefillRef.current = false;
    hasTicketPathPrefillRef.current = true;
  }, []);

  const applySyntheticPrefill = useCallback((pathValue: string) => {
    const parts = pathValue.split(">");
    if (parts.length === 0) {
      return;
    }

    const syntheticSelected = parts.map((choice, index) => ({
      id: -(index + 1),
      choice,
    }));

    setSelected(syntheticSelected);
    setLevelOptions(syntheticSelected.map((option) => [option]));
    selectedPathRef.current = pathValue;
    isSyntheticPrefillRef.current = true;
    hasTicketPathPrefillRef.current = false;
  }, []);

  const hydrateRealLevelsFromValue = useCallback(
    async (pathValue: string) => {
      const path = pathValue.split(">");
      const level1Options = await loadLevel(field.id, 1, 0);

      const restoredSelected: MultiLevelOption[] = [];
      let currentOptions = level1Options;

      for (let levelIndex = 0; levelIndex < path.length; levelIndex += 1) {
        const found = findChoiceMatch(currentOptions, path[levelIndex]);
        if (!found) break;

        restoredSelected[levelIndex] = found;

        if (levelIndex + 1 >= path.length || levelIndex + 1 >= MAX_API_LEVEL) {
          break;
        }

        const nextOptions = await loadLevel(found.id, levelIndex + 2, levelIndex + 1);
        if (nextOptions.length === 0) {
          break;
        }
        currentOptions = nextOptions;
      }

      const hydratedPath = restoredSelected.map((item) => item.choice).join(">");
      setSelected(restoredSelected);
      selectedPathRef.current = hydratedPath || pathValue;
      isSyntheticPrefillRef.current = false;
    },
    [field?.id, loadLevel]
  );

  useEffect(() => {
    selectedPathRef.current = null;
    isSyntheticPrefillRef.current = false;
    hasTicketPathPrefillRef.current = false;
    setSelected([]);
    setLevelLabels({});
    setLevelOptions([undefined]);
  }, [field?.id]);

  useEffect(() => {
    if (!field?.id) {
      return;
    }

    const displayValue =
      value || (ticketPath ? ticketPath.map((item) => item.choice).join(">") : null);

    if (!displayValue) {
      return;
    }

    const normalizedValue = normalizePath(displayValue);
    if (!normalizedValue || normalizedValue === selectedPathRef.current) {
      return;
    }

    if (prefillFromTicketValue) {
      if (ticketPath?.length) {
        applyTicketPathPrefill(ticketPath);
        return;
      }
      applySyntheticPrefill(normalizedValue);
      return;
    }

    let cancelled = false;

    const hydrateFromValue = async () => {
      await hydrateRealLevelsFromValue(normalizedValue);
      if (!cancelled) {
        selectedPathRef.current = normalizedValue;
      }
    };

    void hydrateFromValue();

    return () => {
      cancelled = true;
    };
  }, [
    field?.id,
    value,
    ticketPath,
    prefillFromTicketValue,
    applyTicketPathPrefill,
    applySyntheticPrefill,
    hydrateRealLevelsFromValue,
  ]);

  const handleOpen = async (levelIndex: number) => {
    if (!field?.id) {
      return;
    }

    if (prefillFromTicketValue && hasTicketPathPrefillRef.current) {
      const parentId = levelIndex === 0 ? field.id : selected[levelIndex - 1]?.id;
      if (!parentId || levelOptions[levelIndex]?.length !== 1) {
        return;
      }
      await loadLevel(parentId, levelIndex + 1, levelIndex);
      return;
    }

    if (prefillFromTicketValue && isSyntheticPrefillRef.current) {
      const normalizedValue = value ? normalizePath(value) : selectedPathRef.current;
      if (normalizedValue) {
        await hydrateRealLevelsFromValue(normalizedValue);
      }
      return;
    }

    if (levelIndex !== 0 || levelOptions[levelIndex] !== undefined) {
      return;
    }

    await loadLevel(field.id, 1, 0);
  };

  const handleSelect = async (option: MultiLevelOption, levelIndex: number) => {
    if (prefillFromTicketValue && isSyntheticPrefillRef.current) {
      const normalizedValue = value ? normalizePath(value) : "";
      if (normalizedValue) {
        await hydrateRealLevelsFromValue(normalizedValue);
      }
    }

    hasTicketPathPrefillRef.current = false;

    if (isSyntheticOption(option)) {
      return;
    }

    const newSelected = selected.slice(0, levelIndex);
    newSelected[levelIndex] = option;
    setSelected(newSelected);

    const path = newSelected.map((item) => item.choice).join(">");

    if (levelIndex + 1 >= MAX_API_LEVEL) {
      finalizeSelection(option, path);
      return;
    }

    const nextLevelIndex = levelIndex + 1;

    setLevelOptions((prev) => {
      const next = prev.slice(0, levelIndex + 1);
      next.push(undefined);
      return next;
    });

    const nextOptions = await loadLevel(option.id, nextLevelIndex + 1, nextLevelIndex);

    if (nextOptions.length === 0) {
      finalizeSelection(option, path);
      setLevelOptions((prev) => prev.slice(0, levelIndex + 1));
    }
  };

  return (
    <>
      {levelOptions.map((_options, index) => {
        const options = levelOptions[index] ?? [];
        const fieldLabel = getLevelLabel(index);
        const isLoading = loadingLevel === index;
        const isLoaded = levelOptions[index] !== undefined;

        return (
          <div key={`${field?.id}-level-${index}`} className={className}>
            <Typography variant="subtitle2" style={{ marginLeft: "4px" }}>
              {fieldLabel}
              {index === 0 && field?.is_required && (
                <span style={{ color: "red" }}> *</span>
              )}
            </Typography>
            <Autocomplete
              fullWidth
              options={options}
              value={selected[index] || null}
              loading={isLoading}
              getOptionLabel={(option) => option.choice || ""}
              isOptionEqualToValue={(option, val) => option.id === val.id}
              filterOptions={(opts, { inputValue }) => {
                const query = inputValue.trim().toLowerCase();
                if (!query) return opts;
                return opts.filter((opt) =>
                  opt.choice.toLowerCase().includes(query)
                );
              }}
              onOpen={() => {
                void handleOpen(index);
              }}
              onChange={(_e, newValue) => {
                if (newValue) {
                  void handleSelect(newValue, index);
                }
              }}
              disabled={isLoading || (index > 0 && !selected[index - 1])}
              noOptionsText={
                index === 0 && !isLoaded && !prefillFromTicketValue
                  ? "Open to load options"
                  : "No matching options"
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={`Search / Select ${fieldLabel}`}
                  error={Boolean(error && index === 0)}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            {error && index === 0 && (
              <div style={{ color: "red", marginTop: "4px", marginLeft: "4px" }}>
                {error}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};